import { darken, transparentize } from '@savgroup-front-common/primitive/helpers/cssColor';
import { css, DefaultTheme } from 'styled-components';

import {
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types/Theme';

import { getCustomComponentProperty } from '../../helpers/theme';
import getTextColorBasedOnProps from '../../theme/helpers/getTextColorBasedOnProps';

import { InnerButtonStyledProps } from './Button.types';

interface ColorBasedOnProps {
  theme: DefaultTheme;
  componentThemeName?: string;
  disabled?: boolean;
  isLoading?: boolean;
  hollow?: boolean;
  underline?: boolean;
}

export const colorBasedOnProps = (
  props: ColorBasedOnProps,
  defaultValue?: string | false | null,
) => {
  const { theme, componentThemeName, disabled, isLoading, hollow, underline } =
    props;

  const themeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property:
      disabled && !isLoading
        ? SUPPORTED_PROPERTIES.DISABLED_COLOR
        : SUPPORTED_PROPERTIES.COLOR,
  });

  if (componentThemeName && themeValue && !hollow && !underline) {
    return themeValue;
  }

  return defaultValue || getTextColorBasedOnProps(props);
};

export const getColor = (props: InnerButtonStyledProps) => {
  const {
    theme,
    componentThemeName,
    hollow,
    disabled,
    underline,
    secondary,
    naked,
    tertiary,
    danger,
  } = props;

  const hasWhiteBackground = tertiary || naked;

  let defaultValue = hasWhiteBackground ? undefined : theme.colors.white;

  const themeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.COLOR,
  });

  if (disabled) {
    return theme.colors.white;
  }

  if (hollow && !disabled) {
    defaultValue = undefined;
  }

  if (underline) {
    return colorBasedOnProps(props);
  }

  if (secondary) {
    return theme.newUI.variationColors.dark1;
  }
  if (tertiary) {
    if (danger) {
      return theme.newUI.iconColors.alert.color;
    }

    return theme.newUI.defaultColors.text;
  }

  return themeValue || colorBasedOnProps(props, defaultValue);
};

export const getIconColor = (props: InnerButtonStyledProps) => {
  const {
    theme,
    componentThemeName,
    hollow,
    disabled,
    underline,
    secondary,
    isLoading,
    naked,
    tertiary,
    alert,
    danger,
  } = props;

  const hasWhiteBackground = secondary || tertiary || naked || hollow;

  let defaultValue = hasWhiteBackground ? undefined : theme.colors.white;

  const themeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.COLOR,
    fallback: colorBasedOnProps(props, defaultValue),
  });

  if (disabled && !isLoading) {
    return theme.colors.white;
  }

  if (hollow && !disabled) {
    defaultValue = undefined;
  }

  if (underline) {
    return colorBasedOnProps(props);
  }

  if (alert) {
    return theme.newUI.iconColors.alert.color;
  }

  if (danger && tertiary) {
    return theme.newUI.iconColors.alert.color;
  }

  if (secondary || tertiary) {
    return theme.newUI.defaultColors.secondary;
  }

  return themeValue;
};

export const getButtonPadding = (props: InnerButtonStyledProps) => {
  const { theme, componentThemeName, small, $isIcon, $isMinimal } = props;

  const smallButtonFallback = getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.SMALL_DEFAULT_BUTTONS,
    property: SUPPORTED_PROPERTIES.PADDING,
  });

  if ($isMinimal) {
    return 'unset';
  }

  if (small) {
    return getCustomComponentProperty({
      theme,
      componentName: componentThemeName,
      property: SUPPORTED_PROPERTIES.PADDING,
      fallback: smallButtonFallback,
    });
  }

  if ($isIcon) {
    return '17px 23px 17px 15px';
  }

  return '17px 19px';
};

export const getFontSize = (props: InnerButtonStyledProps) => {
  const { theme, small } = props;

  if (small) {
    return getCustomComponentProperty({
      theme,
      componentName: SUPPORTED_COMPONENTS.SMALL_DEFAULT_BUTTONS,
      property: SUPPORTED_PROPERTIES.FONT_SIZE,
      fallback: theme.fonts.size.ultraSmall,
    });
  }

  return getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.DEFAULT_BUTTONS,
    property: SUPPORTED_PROPERTIES.FONT_SIZE,
    fallback: theme.fonts.size.small,
  });
};

export const getFontWeight = (props: InnerButtonStyledProps) => {
  const { theme, small } = props;

  if (small) {
    return getCustomComponentProperty({
      theme,
      componentName: SUPPORTED_COMPONENTS.SMALL_DEFAULT_BUTTONS,
      property: SUPPORTED_PROPERTIES.FONT_WEIGHT,
    });
  }

  return getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.DEFAULT_BUTTONS,
    property: SUPPORTED_PROPERTIES.FONT_WEIGHT,
  });
};

export const getButtonsTextTransform = (props: InnerButtonStyledProps) => {
  const { theme, componentThemeName } = props;
  const customValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.TEXT_TRANSFORM,
  });

  if (customValue) {
    return customValue;
  }

  return getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.DEFAULT_BUTTONS,
    property: SUPPORTED_PROPERTIES.TEXT_TRANSFORM,
  });
};

export const getWidth = (props: InnerButtonStyledProps) => {
  const { theme, componentThemeName, fluid } = props;

  const customValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.WIDTH,
  });

  if (customValue) {
    return customValue;
  }

  return fluid ? '100%' : 'auto';
};

export const getHeight = (props: InnerButtonStyledProps) => {
  const { theme, componentThemeName, small, $isMinimal } = props;

  const smallDefaultHeight = getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.SMALL_DEFAULT_BUTTONS,
    property: SUPPORTED_PROPERTIES.HEIGHT,
  });

  const defaultHeight = getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
    property: SUPPORTED_PROPERTIES.HEIGHT,
  });

  if ($isMinimal) {
    return 'unset';
  }

  return getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.HEIGHT,
    fallback: small ? smallDefaultHeight : defaultHeight,
  });
};

export const getMinHeight = (props: InnerButtonStyledProps) => {
  const { theme, componentThemeName, small, $isMinimal } = props;

  const smallDefaultMinHeight = getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.SMALL_DEFAULT_BUTTONS,
    property: SUPPORTED_PROPERTIES.MIN_HEIGHT,
  });

  const defaultMinHeight = getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
    property: SUPPORTED_PROPERTIES.MIN_HEIGHT,
  });

  if ($isMinimal) {
    return 'unset';
  }

  return getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.MIN_HEIGHT,
    fallback: small ? smallDefaultMinHeight : defaultMinHeight,
  });
};

export const getOnHoverBackgroundColor = (
  props: InnerButtonStyledProps,
): string | null => {
  const {
    theme,
    componentThemeName,
    disabled,
    primary,
    danger,
    hollow,
    secondary,
    isLoading,
    underline,
    alert,
    tertiary,
    $isMinimal,
  } = props;
  const { interactions = { buttonHoverAsHollow: false } } = theme;

  if ((disabled && !isLoading) || underline || $isMinimal) {
    return null;
  }

  const onHoverCustomThemeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.ON_HOVER_BACKGROUND_COLOR,
  });
  const customThemeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.BACKGROUND_COLOR,
  });

  if (interactions.buttonHoverAsHollow && !hollow) {
    const color = colorBasedOnProps(props);

    return onHoverCustomThemeValue || transparentize(0.7, color);
  }

  if (hollow) {
    const color = colorBasedOnProps(props);
    const backgroundColorOnHover = transparentize(0.9, color);

    return onHoverCustomThemeValue || backgroundColorOnHover;
  }

  if (danger) {
    return darken(0.3, theme.newUI.iconColors.alert.color);
  }
  if (alert) {
    return theme.newUI.iconColors.alert.bgColor;
  }
  if (secondary) {
    return theme.newUI.variationColors.light3;
  }
  if (tertiary) {
    return theme.newUI.variationColors.light6;
  }

  if (onHoverCustomThemeValue) {
    return onHoverCustomThemeValue;
  }
  if (customThemeValue) {
    return darken(0.075, customThemeValue || '');
  }

  const baseColor = getTextColorBasedOnProps(props);

  if (primary || danger) {
    return darken(0.15, baseColor || '');
  }

  if (secondary || tertiary) {
    return theme.systemColors.defaultBackground;
  }

  return darken(0.075, baseColor || '');
};

export const getBackgroundColor = (
  props: InnerButtonStyledProps,
): string | undefined => {
  const {
    theme,
    secondary,
    tertiary,
    componentThemeName,
    disabled,
    hollow,
    isLoading,
    underline,
    $isActive,
  } = props;

  if (disabled && !isLoading) {
    return theme.colors.disabled;
  }

  const themeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.BACKGROUND_COLOR,
  });

  if (hollow) {
    const backgroundColorDisabled = colorBasedOnProps(props);

    if (disabled && !isLoading) {
      return themeValue || backgroundColorDisabled;
    }

    if ($isActive) {
      return getOnHoverBackgroundColor(props) ?? 'transparent';
    }

    return themeValue || 'transparent';
  }

  if (themeValue && !hollow && !underline) {
    return themeValue;
  }
  if (secondary) {
    return theme.newUI.variationColors.light5;
  }
  if (tertiary) {
    return theme.colors.neutralBackground;
  }

  const baseColor = getTextColorBasedOnProps(props);

  if (isLoading) {
    return getBackgroundColor({ ...props, isLoading: false, disabled: false });
  }

  return baseColor;
};

export const getBorder = (
  props: InnerButtonStyledProps,
  defaultValue = '1px solid transparent',
) => {
  const {
    theme,
    componentThemeName,
    disabled,
    hollow,
    tertiary,
    isLoading,
    $isPressed,
  } = props;
  let result = defaultValue;

  if (disabled && !isLoading) {
    return `1px solid transparent`;
  }

  const customBorder = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.BORDER,
  });

  if (hollow) {
    if (isLoading) {
      const hollowBorderColor = colorBasedOnProps({
        ...props,
        isLoading: false,
        disabled: false,
      });

      result = customBorder || `1px solid ${hollowBorderColor}`;
    } else {
      const hollowBorderColor = colorBasedOnProps(props);

      result = customBorder || `1px solid ${hollowBorderColor}`;
    }
  } else if (customBorder) {
    result = customBorder;
  }

  if (tertiary) {
    result = `1px solid ${
      $isPressed
        ? theme.newUI.variationColors.light1
        : theme.newUI.variationColors.light3
    }`;
  }

  return result;
};

export const getOnHoverColor = (props: InnerButtonStyledProps) => {
  const {
    theme,
    componentThemeName,
    hollow,
    disabled,
    underline,
    secondary,
    tertiary,
    naked,
    danger,
    $isSquareIconButton,
  } = props;
  const { interactions = { buttonHoverAsHollow: false } } = theme;

  if (naked || disabled) {
    return null;
  }

  let defaultValue = secondary || tertiary ? undefined : theme.colors.white;
  const themeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.ON_HOVER_COLOR,
  });

  if (interactions.buttonHoverAsHollow) {
    return themeValue || null;
  }

  if (danger && tertiary) {
    return theme.newUI.iconColors.alert.color;
  }

  if (secondary && $isSquareIconButton) {
    return theme.newUI.defaultColors.secondary;
  }

  if (themeValue) {
    return themeValue;
  }

  if (hollow && !disabled) {
    defaultValue = undefined;
  }
  if (underline) {
    return darken(0.3, colorBasedOnProps(props));
  }

  return colorBasedOnProps(props, defaultValue);
};
export const getOnHoverBorder = (
  props: InnerButtonStyledProps,
  defaultValue?: string,
): string | undefined => {
  const {
    theme,
    componentThemeName,
    disabled,
    secondary,
    isLoading,
    danger,
    tertiary,
  } = props;

  let result = defaultValue || getBorder(props);

  const { interactions = { buttonHoverAsHollow: false } } = theme;

  if (disabled && !isLoading) {
    return undefined;
  }

  const themeValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.ON_HOVER_BORDER,
  });

  if (themeValue) {
    result = themeValue;
  }

  if (secondary) {
    return result;
  }
  if (tertiary && danger) {
    return `1px solid ${theme.newUI.iconColors.alert.color}`;
  }
  if (tertiary) {
    return `1px solid ${theme.newUI.defaultColors.secondary}`;
  }

  if (interactions.buttonHoverAsHollow) {
    return themeValue;
  }

  return result;
};

export const getBorderRadius = (props: InnerButtonStyledProps) => {
  const { theme, rounded } = props;

  if (rounded) {
    return null;
  }

  return theme.newUI.borders.smallRadius;
};

export const underlineColorBasedOnProps = (props: InnerButtonStyledProps) => {
  if (props.disabled && !props.isLoading) {
    return null;
  }
  if (props.link) {
    return darken(0.1, getTextColorBasedOnProps(props));
  }

  return darken(0.1, colorBasedOnProps(props));
};

export const handleUnderlineProps = (props: InnerButtonStyledProps) => {
  if (!props.underline) {
    return '';
  }

  const color = colorBasedOnProps(props);
  const colorOnHover =
    props.disabled && !props.isLoading ? null : darken(0.1, color);
  const colorOnFocus =
    props.disabled && !props.isLoading ? null : darken(0.1, color);

  return css`
    color: ${color};
    padding: 0;
    text-decoration: underline;
    background-color: transparent;

    &:hover {
      color: ${colorOnHover};
    }
    &:focus {
      color: ${colorOnFocus};
    }
    ${props.link
      ? css`
          a {
            text-decoration: none !important;
            color: ${props.disabled && !props.isLoading
              ? darken(0.1, colorBasedOnProps(props))
              : getTextColorBasedOnProps(props)};
            cursor: ${props.disabled && !props.isLoading
              ? 'not-allowed'
              : 'pointer'};
            pointer-events: ${props.disabled && !props.isLoading
              ? 'none'
              : null};

            &:hover,
            &:focus {
              color: ${darken(0.1, getTextColorBasedOnProps(props))};
            }
          }
        `
      : null};
  `;
};

export const getWhiteSpace = (props: InnerButtonStyledProps) => {
  const { componentThemeName, theme } = props;

  const customValue = getCustomComponentProperty({
    theme,
    componentName: componentThemeName,
    property: SUPPORTED_PROPERTIES.WHITE_SPACE,
  });

  return customValue || 'nowrap';
};
